import React, { useState, useEffect, useMemo } from 'react';
import {
  Menu,
  X,
  Home,
  Users,
  ShoppingCart,
  CreditCard,
  Settings,
  DollarSign,
  FileText,
  BarChart3,
  Calendar,
  PieChart,
  UserCog,
  LogOut,
  User,
  ChevronDown,
  Plane,
  UsersRound,
  Briefcase,
  Receipt,
  MessageSquare,
} from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from './ConfirmationModal';
import useConfirmation from '../../hooks/useConfirmation';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';

const getMenuItems = (t) => [
  { text: t('navigation.home'), icon: Home, path: '/' },
  { text: t('navigation.createGroup'), icon: Users, path: '/create-group' },
  { text: t('navigation.SalesPage'), icon: UsersRound, path: '/SalesPage' },
  { text: t('navigation.PaymentsPage'), icon: Users, path: '/PaymentsPage' },
  // { text: t('navigation.buySell'), icon: ShoppingCart, path: '/buy-sell' },
  // { text: t('navigation.receiveCash'), icon: CreditCard, path: '/receive-cash' },
  { text: t('navigation.manageGroups'), icon: Settings, path: '/manage-groups' },
  { text: 'تقرير صرفيات المسافرين', icon: Receipt, path: '/reports/traveler-expenses' },
  { text: 'إيرادات وصرفيات المجموعات', icon: BarChart3, path: '/reports/group-revenues-expenses' },
  // { text: t('travelers.title'), icon: Plane, path: '/travelers' },
  // Collapse group: Balances
  {
    key: 'balances-group',
    text: t('navigation.balancesGroup') || 'الأرصدة',
    icon: DollarSign,
    children: [
      { text: t('navigation.balances'), icon: DollarSign, path: '/balances' },
      { text: t('navigation.Balancestravelers'), icon: DollarSign, path: '/Balancestravelers' },
      { text: t('navigation.Balancesagents'), icon: DollarSign, path: '/Balancesagents' },
      { text: t('navigation.Balancessupervisors'), icon: DollarSign, path: '/Balancessupervisors' },
    ],
  },
  // Collapse group: Account Statements
  {
    key: 'statements-group',
    text: t('navigation.accountStatementsGroup') || 'كشف الحساب',
    icon: FileText,
    children: [
      // { text: t('navigation.accountStatement'), icon: FileText, path: '/account-statement' },
      { text: t('navigation.TravelersLedger'), icon: FileText, path: '/TravelersLedger' },
      { text: t('navigation.ReceivedPayments'), icon: FileText, path: '/received-payments' },
    ],
  },
  { text: t('navigation.InstallmentsOngoing') || 'الأقساط الجارية', icon: BarChart3, path: '/installments-ongoing' },
  { text: t('navigation.InstallmentsQuery'), icon: BarChart3, path: '/InstallmentsQuery' },
  // { text: t('navigation.groupReport'), icon: BarChart3, path: '/group-report' },
  { text: t('navigation.activegroups'), icon: BarChart3, path: '/active-groups' },
  { divider: true },
  // { text: t('navigation.dailySchedule'), icon: Calendar, path: '/daily-schedule' },
  // { text: t('navigation.reports'), icon: PieChart, path: '/reports' },
  // Collapse group: Twilio Reports
  {
    key: 'twilio-group',
    text: 'تقارير الرسائل',
    icon: MessageSquare,
    children: [
      { text: 'تقرير الرسائل', icon: MessageSquare, path: '/message-reports' },
      { text: 'المستقبلون المفقودون', icon: Users, path: '/missing-recipients' },
    ],
  },
  // Collapse group: Management
  {
    key: 'management-group',
    text: t('navigation.managementGroup') || 'الإدارة',
    icon: Briefcase,
    children: [
      { text: t('navigation.manageUsers'), icon: UserCog, path: '/manage-users' },
      { text: t('navigation.GroupsPaymentPage'), icon: Users, path: '/GroupsPaymentPage' },
      { text: t('navigation.generalPayments'), icon: Receipt, path: '/general-payments' },
      { text: t('navigation.AgentsPage'), icon: Users, path: '/AgentsPage' },
      { text: t('navigation.TravelersPage'), icon: Users, path: '/TravelersPage' },
      { text: t('navigation.SupervisorsPage'), icon: Users, path: '/SupervisorsPage' },
      // { text: t('payments.title'), icon: CreditCard, path: '/payments' },
    ],
  },
];

const Layout = ({ children, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const { confirmationState, hideConfirmation, confirmLogout } = useConfirmation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Collapsible groups state
  const balanceRoutes = useMemo(() => ['/balances', '/Balancestravelers', '/Balancesagents', '/Balancessupervisors'], []);
  const statementRoutes = useMemo(() => ['/account-statement', '/TravelersLedger'], []);
  const managementRoutes = useMemo(() => ['/manage-users', '/GroupsPaymentPage', '/general-payments', '/AgentsPage', '/TravelersPage', '/SupervisorsPage', '/payments'], []);
  const twilioRoutes = useMemo(() => ['/message-reports', '/missing-recipients'], []);
  const [balancesOpen, setBalancesOpen] = useState(() => balanceRoutes.includes(location.pathname));
  const [statementsOpen, setStatementsOpen] = useState(() => statementRoutes.includes(location.pathname));
  const [managementOpen, setManagementOpen] = useState(() => managementRoutes.includes(location.pathname));
  const [twilioOpen, setTwilioOpen] = useState(() => twilioRoutes.includes(location.pathname));

  // Auto-open groups when visiting child routes
  useEffect(() => {
    if (balanceRoutes.includes(location.pathname)) setBalancesOpen(true);
    if (statementRoutes.includes(location.pathname)) setStatementsOpen(true);
    if (managementRoutes.includes(location.pathname)) setManagementOpen(true);
    if (twilioRoutes.includes(location.pathname)) setTwilioOpen(true);
  }, [location.pathname, balanceRoutes, statementRoutes, managementRoutes, twilioRoutes]);

  const handleMenuClick = (path) => {
    navigate(path);
    setSidebarOpen(false); // Close mobile sidebar after navigation
  };

  const handleLogout = () => {
    setProfileMenuOpen(false);

    confirmLogout(() => {
      localStorage.removeItem("auth_token"); // امسح التوكن
      toast.success("تم تسجيل الخروج بنجاح");
      window.location.href = "/login"; // رجع المستخدم للـ login
    });
  };


  const menuItems = getMenuItems(t);

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Logo */}
      <div className="flex items-center justify-center h-16 px-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <img
            src="/ajaba-logo.png"
            alt="App Logo"
            className="w-12 h-12 rounded-lg object-contain"
          />
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 pr-4">{t('common.appName')}</h1>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {menuItems.map((item, index) => {
          if (item.divider) {
            return (
              <div key={`divider-${index}`} className="border-t border-gray-200 dark:border-gray-700 my-4" />
            );
          }

          const Icon = item.icon;

          // Render collapsible group
          if (item.children && Array.isArray(item.children)) {
            const groupKey = item.key || `group-${index}`;
            const isGroupActive = item.children.some((child) => location.pathname === child.path);
            const isOpen = groupKey === 'balances-group' ? balancesOpen : groupKey === 'statements-group' ? statementsOpen : groupKey === 'management-group' ? managementOpen : groupKey === 'twilio-group' ? twilioOpen : false;
            const toggleOpen = () => {
              if (groupKey === 'balances-group') setBalancesOpen(!isOpen);
              else if (groupKey === 'statements-group') setStatementsOpen(!isOpen);
              else if (groupKey === 'management-group') setManagementOpen(!isOpen);
              else if (groupKey === 'twilio-group') setTwilioOpen(!isOpen);
            };

            return (
              <div key={groupKey} className="space-y-1">
                <button
                  onClick={toggleOpen}
                  className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group ${isGroupActive
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-r-2 border-primary-600 dark:border-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                >
                  <Icon
                    className={`w-5 h-5 mr-3 rtl:ml-3 rtl:mr-0 transition-colors ${isGroupActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                      }`}
                  />
                  <span className="truncate">{item.text}</span>
                  <ChevronDown className={`w-4 h-4 ml-auto rtl:mr-auto rtl:ml-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div className="ml-8 rtl:mr-8 rtl:ml-0 space-y-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon || Icon;
                      const childActive = location.pathname === child.path;
                      return (
                        <Link
                          key={child.path || child.text}
                          to={child.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 group ${childActive
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-r-2 border-primary-600 dark:border-primary-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                            }`}
                        >
                          <ChildIcon
                            className={`w-4 h-4 mr-3 rtl:ml-3 rtl:mr-0 transition-colors ${childActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                              }`}
                          />
                          <span className="truncate">{child.text}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Render regular item
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path || item.text}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group ${isActive
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-r-2 border-primary-600 dark:border-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
            >
              <Icon
                className={`w-5 h-5 mr-3 rtl:ml-3 rtl:mr-0 transition-colors ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                  }`}
              />
              <span className="truncate">{item.text}</span>
            </Link>
          );
        })}
      </nav>

      {/* User Profile Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{t('common.adminUser')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{t('common.adminEmail')}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 rtl:right-0 rtl:left-auto z-50 w-72 bg-white dark:bg-gray-900 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 rtl:lg:-translate-x-0 lg:static lg:inset-0 ${sidebarOpen ? 'translate-x-0 rtl:-translate-x-0' : '-translate-x-full rtl:translate-x-full'
          }`}
      >
        {/* Mobile close button */}
        <div className="lg:hidden absolute top-4 right-4">
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <SidebarContent />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <img
              src="/ajaba-logo.png"
              alt="App Logo"
              className="w-12 h-12 rounded-lg object-contain"
            />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t('common.appTitle')}
            </h1>
          </div>

            {/* Theme and Language toggles + Profile dropdown */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Language Toggle */}
              <LanguageToggle />

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center space-x-3 rtl:space-x-reverse p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* Dropdown menu */}
                {profileMenuOpen && (
                  <div className="absolute right-0 rtl:left-0 rtl:right-auto top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      {t('navigation.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
          <div className="p-4 sm:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={confirmationState.open}
        onClose={hideConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        type={confirmationState.type}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        confirmColor={confirmationState.confirmColor}
      />
    </div>
  );
};

export default Layout;
